'use client';

import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { Section } from '@/components/ui/Section';
import styles from './TabletShowcase.module.css';

export interface TabletShowcaseProps {
  title: string;
  subtitle: string;
  altTexts?: {
    drinks?: string;
    food?: string;
    product?: string;
    categories?: string;
    brasserieMenu?: string;
    ekartMenu?: string;
    mockup?: string;
  };
}

/**
 * TabletShowcase Component
 * Displays all 4 tablet ordering screens with floating animation effect
 * On mobile: auto-playing slider with touch support
 */
export function TabletShowcase({ title, subtitle, altTexts }: TabletShowcaseProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Detect mobile and desktop for smart image loading
  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsDesktop(width > 768);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Auto-play slider on mobile
  useEffect(() => {
    if (!isMobile) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 4);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [isMobile]);

  // ✅ PERFORMANCE FIX: Optimized animations for mobile devices
  useEffect(() => {
    const isSlowDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isSlowDevice) {
      const tablets = sectionRef.current?.querySelectorAll(`.${styles.tablet}`);
      tablets?.forEach((tablet) => {
        tablet.classList.add(styles.visible);
      });
      return;
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1 }
    );

    const tablets = sectionRef.current?.querySelectorAll(`.${styles.tablet}`);
    tablets?.forEach((tablet) => observer.observe(tablet));

    return () => observer.disconnect();
  }, []);

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // Minimum swipe distance

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swiped left - next slide
        setCurrentSlide((prev) => (prev + 1) % 4);
      } else {
        // Swiped right - previous slide
        setCurrentSlide((prev) => (prev - 1 + 4) % 4);
      }
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  const tablets = [
    {
      id: 'charlie',
      src: '/images/tablet-variants/tablet-ordering-charlie-restaurant-drinks.webp',
      alt: altTexts?.drinks || 'Jamezz tablet ordering - Charlie Restaurant menu',
      color: '#d2a253',
    },
    {
      id: 'brasserie',
      src: '/images/tablet-case-studies/tablet-brasserie-bij-ons-menu.webp',
      alt: altTexts?.brasserieMenu || 'Jamezz tablet ordering - Brasserie bij Ons menu',
      color: '#76b98f',
    },
    {
      id: 'ekart',
      src: '/images/tablet-case-studies/tablet-ekart-menu.webp',
      alt: altTexts?.ekartMenu || 'Jamezz tablet ordering - E-Kart menu',
      color: '#fcdb21',
    },
    {
      id: 'mockup',
      src: '/images/mockups/tablet-mockup.webp',
      alt: altTexts?.mockup || 'Jamezz tablet ordering system mockup',
      color: '#f36e31',
    },
  ];

  return (
    <Section background="primary" paddingY="none">
      <div className={styles.showcase} ref={sectionRef}>
      <div className={styles.background}>
        <div className={styles.gradientOrb1} />
        <div className={styles.gradientOrb2} />
        <div className={styles.grid} />
      </div>

      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div 
          className={styles.tabletsGrid}
          ref={sliderRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={isMobile ? {
            transform: `translateX(-${currentSlide * 100}%)`,
            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          } : {}}
        >
          {tablets.map((tablet, index) => (
            <div
              key={tablet.id}
              className={styles.tablet}
              style={{
                animationDelay: `${index * 0.15}s`,
              }}
            >
              <div className={styles.tabletInner}>
                <div
                  className={styles.tabletGlow}
                  style={{
                    background: `radial-gradient(circle at center, ${tablet.color}40 0%, transparent 70%)`,
                  }}
                />
                <div className={styles.imageWrapper}>
                  <Image
                    src={tablet.src}
                    alt={tablet.alt}
                    width={400}
                    height={800}
                    className={styles.tabletImage}
                    loading={
                      isDesktop 
                        ? (index < 2 ? "eager" : "lazy")  // Desktop: eerste 2 eager
                        : (index === 0 ? "eager" : "lazy")  // Mobile: alleen eerste eager
                    }
                    priority={index === 0}
                    quality={85}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation dots - CSS hides on desktop, visible immediately on mobile */}
          <div className={styles.sliderDots}>
            {tablets.map((_, index) => (
              <button
                key={index}
                className={`${styles.dot} ${index === currentSlide ? styles.dotActive : ''}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
      </div>
      </div>
    </Section>
  );
}

